/* ═══════════════════════════════════════════════════════════════════════════
   Le kit de découpe — la grammaire de forme du jeu (PLAN.md §5.3, J13).

   Tout ce qui vole dans NEON DESCENT sort d'UN seul générateur : un contour 2D
   fermé, « taillé » en carène à facettes. Le contour donne la SILHOUETTE, la
   carène donne le volume, et les facettes plates donnent la lecture de lumière.

   Pourquoi un générateur plutôt que des fichiers :

     1. Zéro octet de réseau. Le budget de bundle critique est de 1 Mo alors que
        three seul en pèse 155 en gzip ; chaque .glb évité est du budget rendu
        au code. Une coque décrite par huit couples de nombres coûte quelques
        dizaines d'octets DANS le bundle, contre quelques dizaines de kilooctets
        à télécharger.
     2. Déclinaison par paramètre. Le motif « 6 meshes de base + 4 attachements
        → 10 ennemis » du plan est trivial quand un mesh est une fonction : on
        compose, on ne modélise pas dix fois.
     3. Cohérence par CONSTRUCTION. Un solo sans artiste ne produit pas une
        direction artistique en dessinant mieux ; il la produit en s'interdisant
        des choses. Ici l'interdit est simple — aucune coque n'existe hors de ce
        fichier, donc toutes partagent la même facture.

   Ce module ne connaît PAS three. Il produit des tableaux de nombres, ce qui le
   rend testable en Node sans contexte GPU — et c'est la seule raison pour
   laquelle ses invariants (orientation des faces, cellules de palette valides)
   peuvent tourner en CI.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Un sommet de contour, en unités normalisées : le contour tient dans le
 *  disque de rayon 1, et l'échelle réelle est appliquée à l'instanciation. */
export type Vec2 = readonly [number, number];

/** Géométrie non indexée, prête à devenir un `BufferGeometry`.
 *
 *  ⚠️ NON INDEXÉE, et c'est voulu : les normales sont PLATES (une par
 *  triangle), donc deux triangles adjacents ne peuvent pas partager un sommet.
 *  Indexer imposerait de lisser, et l'arête vive est précisément ce qui fait
 *  lire le volume sur des coques de quarante triangles. */
export interface MeshData {
  readonly position: number[];
  readonly normal: number[];
  readonly uv: number[];
}

export function emptyMesh(): MeshData {
  return { position: [], normal: [], uv: [] };
}

export function triangleCount(m: MeshData): number {
  return m.position.length / 9;
}

/* ── L'atlas de palette, côté coordonnées ──────────────────────────────────
   L'atlas est une grille de `ATLAS_GRID` × `ATLAS_GRID` cellules. Un sommet ne
   porte pas une couleur : il porte l'UV du CENTRE de sa cellule.

   ⚠️ Le centre, pas un coin. Avec un filtrage au plus proche voisin et aucun
   mipmap, viser le centre rend l'échantillonnage exact quelle que soit la
   précision du rastériseur. Viser un bord produirait un bavage d'une cellule
   sur l'autre, visible comme des pixels orange isolés sur une coque grise —
   symptôme classique, diagnostiqué comme un bug de shader alors que c'est un
   demi-texel. */
export const ATLAS_GRID = 4;

export function cellUv(cell: number): Vec2 {
  const c = cell % ATLAS_GRID;
  const r = Math.floor(cell / ATLAS_GRID);
  return [(c + 0.5) / ATLAS_GRID, 1 - (r + 0.5) / ATLAS_GRID];
}

/* ── Le générateur ──────────────────────────────────────────────────────── */

export interface CrystalOptions {
  /** Hauteur de l'arête faîtière, vers la caméra. */
  readonly ridge: number;
  /** Profondeur de la quille, à l'opposé. Généralement plus faible : la face
   *  arrière n'est jamais vue de face et ne mérite pas ses triangles. */
  readonly keel: number;
  /** Cellule de l'atlas de palette. */
  readonly cell: number;
}

const EPS = 1e-4;

function pushTri(
  out: MeshData,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
  u: number, v: number,
): void {
  // Normale plate par produit vectoriel. Un triangle dégénéré donnerait une
  // normale nulle, donc un facette noire : on le laisse tomber.
  const ux = bx - ax, uy = by - ay, uz = bz - az;
  const vx = cx - ax, vy = cy - ay, vz = cz - az;
  let nx = uy * vz - uz * vy;
  let ny = uz * vx - ux * vz;
  let nz = ux * vy - uy * vx;
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
  if (len < EPS) return;
  nx /= len; ny /= len; nz /= len;

  out.position.push(ax, ay, az, bx, by, bz, cx, cy, cz);
  out.normal.push(nx, ny, nz, nx, ny, nz, nx, ny, nz);
  out.uv.push(u, v, u, v, u, v);
}

/**
 * Taille une coque à facettes à partir d'un contour fermé.
 *
 * Le contour est posé à z = 0 — c'est la ligne de plus grande largeur, donc la
 * SILHOUETTE vue de la caméra fixe. Chaque arête du contour est reliée à une
 * « épine » verticale posée sur x = 0, une fois vers l'avant (+ridge) et une
 * fois vers l'arrière (−keel). Le résultat est une carène symétrique, à arêtes
 * vives, dont la lecture ne dépend d'aucune texture.
 *
 * ⚠️ Le contour doit être ORIENTÉ DANS LE SENS TRIGONOMÉTRIQUE (vu depuis +Z)
 * et rester étoilé par rapport à l'axe x = 0 : une échancrure qui traverse
 * l'axe produirait des facettes retournées, invisibles au débogage parce que le
 * back-face culling les efface simplement. Un test de CI vérifie l'orientation
 * de chaque contour du catalogue ; la seconde condition est tenue par la règle
 * de style « les contours de base sont convexes, le détail vient des
 * attachements ».
 */
export function crystal(outline: readonly Vec2[], opts: CrystalOptions): MeshData {
  const out = emptyMesh();
  const [u, v] = cellUv(opts.cell);
  const n = outline.length;

  for (let i = 0; i < n; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % n]!;
    const ax = a[0], ay = a[1];
    const bx = b[0], by = b[1];

    // Points d'épine : même ordonnée que le sommet de contour, sur x = 0.
    const sameSpine = Math.abs(ay - by) < EPS;

    // Avant.
    if (sameSpine) {
      pushTri(out, ax, ay, 0, bx, by, 0, 0, by, opts.ridge, u, v);
    } else {
      pushTri(out, ax, ay, 0, bx, by, 0, 0, by, opts.ridge, u, v);
      pushTri(out, ax, ay, 0, 0, by, opts.ridge, 0, ay, opts.ridge, u, v);
    }

    // Arrière : mêmes triangles, ordre inversé et épine négative.
    if (sameSpine) {
      pushTri(out, 0, by, -opts.keel, bx, by, 0, ax, ay, 0, u, v);
    } else {
      pushTri(out, 0, by, -opts.keel, bx, by, 0, ax, ay, 0, u, v);
      pushTri(out, 0, ay, -opts.keel, 0, by, -opts.keel, ax, ay, 0, u, v);
    }
  }

  return out;
}

/**
 * Un anneau plat à facettes — la seule forme que le générateur de contour ne
 * sait pas produire, parce qu'elle est trouée et donc jamais étoilée.
 *
 * Il sert de bouclier (Égide), de ceinture de boss et de collerette de tourelle.
 * C'est l'attachement qui se lit le mieux de loin : un cercle est la seule forme
 * qu'on identifie encore à douze pixels.
 */
export function ringPlate(
  rInner: number,
  rOuter: number,
  segments: number,
  thickness: number,
  cell: number,
): MeshData {
  const out = emptyMesh();
  const [u, v] = cellUv(cell);
  const step = (Math.PI * 2) / segments;

  for (let i = 0; i < segments; i++) {
    const a0 = i * step;
    const a1 = (i + 1) * step;
    const c0 = Math.cos(a0), s0 = Math.sin(a0);
    const c1 = Math.cos(a1), s1 = Math.sin(a1);

    for (const z of [thickness, -thickness]) {
      const flip = z < 0;
      const i0x = c0 * rInner, i0y = s0 * rInner;
      const i1x = c1 * rInner, i1y = s1 * rInner;
      const o0x = c0 * rOuter, o0y = s0 * rOuter;
      const o1x = c1 * rOuter, o1y = s1 * rOuter;
      if (flip) {
        pushTri(out, i0x, i0y, z, o1x, o1y, z, o0x, o0y, z, u, v);
        pushTri(out, i0x, i0y, z, i1x, i1y, z, o1x, o1y, z, u, v);
      } else {
        pushTri(out, i0x, i0y, z, o0x, o0y, z, o1x, o1y, z, u, v);
        pushTri(out, i0x, i0y, z, o1x, o1y, z, i1x, i1y, z, u, v);
      }
    }

    // Tranche extérieure : sans elle, l'anneau disparaît dès qu'il est vu par
    // la tranche, et il l'est en permanence sur une caméra à 30° de FOV.
    pushTri(out, c0 * rOuter, s0 * rOuter, thickness, c0 * rOuter, s0 * rOuter, -thickness, c1 * rOuter, s1 * rOuter, -thickness, u, v);
    pushTri(out, c0 * rOuter, s0 * rOuter, thickness, c1 * rOuter, s1 * rOuter, -thickness, c1 * rOuter, s1 * rOuter, thickness, u, v);
  }

  return out;
}

/* ── Composition ────────────────────────────────────────────────────────── */

export interface Placement {
  readonly tx?: number;
  readonly ty?: number;
  readonly tz?: number;
  readonly scale?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  /** Rotation autour de Z, en radians. */
  readonly rot?: number;
  /** Symétrie par rapport à x = 0. ⚠️ Elle INVERSE l'orientation des faces :
   *  l'enroulement et les normales sont recalculés en conséquence, sans quoi la
   *  moitié miroir d'un vaisseau serait éclairée à l'envers — défaut discret,
   *  qui se lit comme « ce côté est plus sombre » sans qu'on sache pourquoi. */
  readonly mirrorX?: boolean;
}

export function place(m: MeshData, p: Placement): MeshData {
  const out = emptyMesh();
  const s = p.scale ?? 1;
  const sx = (p.scaleX ?? 1) * s * (p.mirrorX === true ? -1 : 1);
  const sy = (p.scaleY ?? 1) * s;
  const sz = s;
  const rot = p.rot ?? 0;
  const cr = Math.cos(rot), sr = Math.sin(rot);
  const tx = p.tx ?? 0, ty = p.ty ?? 0, tz = p.tz ?? 0;
  const tris = triangleCount(m);

  for (let t = 0; t < tris; t++) {
    const b = t * 9;
    const xs: number[] = [];
    const ys: number[] = [];
    const zs: number[] = [];
    for (let k = 0; k < 3; k++) {
      const x0 = m.position[b + k * 3]! * sx;
      const y0 = m.position[b + k * 3 + 1]! * sy;
      const z0 = m.position[b + k * 3 + 2]! * sz;
      xs.push(x0 * cr - y0 * sr + tx);
      ys.push(x0 * sr + y0 * cr + ty);
      zs.push(z0 + tz);
    }
    // ⚠️ L'ordre des sommets est inversé quand le DÉTERMINANT de la
    // transformation est négatif — pas quand `scaleX` l'est. Deux échelles
    // négatives forment une rotation d'un demi-tour, qui préserve
    // l'orientation : tester le seul `sx` retournerait toute une flotte
    // hostile à l'envers, back-face culling compris, et le symptôme serait
    // « les ennemis sont invisibles » sans aucune erreur.
    const order = sx * sy * sz < 0 ? [0, 2, 1] : [0, 1, 2];
    const uvBase = t * 6;
    pushTri(
      out,
      xs[order[0]!]!, ys[order[0]!]!, zs[order[0]!]!,
      xs[order[1]!]!, ys[order[1]!]!, zs[order[1]!]!,
      xs[order[2]!]!, ys[order[2]!]!, zs[order[2]!]!,
      m.uv[uvBase]!, m.uv[uvBase + 1]!,
    );
  }

  return out;
}

export function merge(...parts: readonly MeshData[]): MeshData {
  const out = emptyMesh();
  for (const p of parts) {
    out.position.push(...p.position);
    out.normal.push(...p.normal);
    out.uv.push(...p.uv);
  }
  return out;
}

/** Aire signée d'un contour. Positive ⇔ sens trigonométrique. Sert au test de
 *  CI qui interdit un contour saisi à l'envers — panne dont le seul symptôme
 *  serait un vaisseau intégralement invisible, le back-face culling l'ayant
 *  proprement effacé. */
export function signedArea(outline: readonly Vec2[]): number {
  let a = 0;
  for (let i = 0; i < outline.length; i++) {
    const p = outline[i]!;
    const q = outline[(i + 1) % outline.length]!;
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

/** Rayon englobant d'un contour. Sert à vérifier que le catalogue reste
 *  normalisé : deux vaisseaux au même `visualRadius` doivent occuper la même
 *  place à l'écran, sans quoi la hurtbox cesse d'être lisible dans le visuel. */
export function outlineRadius(outline: readonly Vec2[]): number {
  let r = 0;
  for (const p of outline) {
    const d = Math.sqrt(p[0] * p[0] + p[1] * p[1]);
    if (d > r) r = d;
  }
  return r;
}
